import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, Pencil } from 'lucide-react';
import { ContributorArcheMetric } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface ArcheMetricsTableProps {
  arches: ContributorArcheMetric[];
  loading: boolean;
}

type SortField = 'name' | 'invocationCount' | 'successRate' | 'estimatedRevenueCents' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

export function ArcheMetricsTable({ arches, loading }: ArcheMetricsTableProps) {
  const [sortField, setSortField] = useState<SortField>('updatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedArches = useMemo(() => {
    let filtered = arches;

    if (statusFilter !== 'all') {
      filtered = arches.filter((a) => a.status === statusFilter);
    }

    return [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'invocationCount':
          aVal = a.invocationCount;
          bVal = b.invocationCount;
          break;
        case 'successRate':
          aVal = a.successRate;
          bVal = b.successRate;
          break;
        case 'estimatedRevenueCents':
          aVal = a.estimatedRevenueCents;
          bVal = b.estimatedRevenueCents;
          break;
        case 'updatedAt':
          aVal = a.updatedAt;
          bVal = b.updatedAt;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [arches, sortField, sortDirection, statusFilter]);

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead
      onClick={() => handleSort(field)}
      className="cursor-pointer hover:bg-accent/50 transition-colors"
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field &&
          (sortDirection === 'asc' ? (
            <ArrowUp className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ArrowDown className="h-3 w-3 text-muted-foreground" />
          ))}
      </div>
    </TableHead>
  );

  const getStatusClasses = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'testing':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'deprecated':
        return 'bg-destructive/20 text-destructive';
      case 'suspended':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* Filter */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Label htmlFor="statusFilter" className="text-sm">
            Filter by status:
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="statusFilter" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="testing">Testing</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="deprecated">Deprecated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden">
        {sortedArches.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No arches found</div>
        ) : (
          <div className="divide-y divide-border">
            {sortedArches.map((arche) => (
              <div key={arche.archId} className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium truncate">{arche.name}</h3>
                      {arche.editSessionId && (
                        <Link
                          to={`/a/arche_plugin_builder/${arche.editSessionId}`}
                          className="text-muted-foreground hover:text-primary shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {arche.simpleDescription}
                    </p>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn('capitalize shrink-0', getStatusClasses(arche.status))}
                  >
                    {arche.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Pricing: </span>
                    <span>{arche.pricingLabel}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Invocations: </span>
                    <span>{arche.invocationCount.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Success: </span>
                    <span>{(arche.successRate * 100).toFixed(1)}%</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Revenue: </span>
                    <span>${(arche.estimatedRevenueCents / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHeader field="name">Name</SortHeader>
              <TableHead>Status</TableHead>
              <TableHead>Pricing</TableHead>
              <SortHeader field="invocationCount">Invocations</SortHeader>
              <SortHeader field="successRate">Success</SortHeader>
              <SortHeader field="estimatedRevenueCents">Revenue</SortHeader>
              <SortHeader field="updatedAt">Updated</SortHeader>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedArches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No arches found
                </TableCell>
              </TableRow>
            ) : (
              sortedArches.map((arche) => (
                <TableRow key={arche.archId}>
                  <TableCell>
                    <div className="font-medium">{arche.name}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {arche.simpleDescription}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn('capitalize', getStatusClasses(arche.status))}
                    >
                      {arche.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{arche.pricingLabel}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {arche.invocationCount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(arche.successRate * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell className="font-medium">
                    ${(arche.estimatedRevenueCents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(arche.updatedAt)}
                  </TableCell>
                  <TableCell>
                    {arche.editSessionId && (
                      <Link
                        to={`/a/arche_plugin_builder/${arche.editSessionId}`}
                        className="text-muted-foreground hover:text-primary"
                        title="Edit in builder"
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
